import type { Kitchen } from '../../../kitchens/kitchen.class';
import type { Panel } from '../../../panels/panel.class';
import type { Island } from '../../../islands/island.class';
import type { PanelItemGroup } from '../../../panel-item-groups/panel-item-group.class';
import { cleanIslandCfn } from '../../../islands/functions/clean-island.cfn';
import { placeGroupCarryCfn, removeGroupCfn, takeGroupCarryCfn } from './move-appliance-group-to-panel.cfn';
import { hypotheticalGroupWidthsCfn, predictPanelGrowthCfn, resolveGrowthCfn } from './predict-panel-growth.cfn';
import { IslandResize, RearrangeGrowthOptions, SwapGroupsAcrossPanelsResult } from './rearrange.types';

export interface SwapAcrossPanelsOptions {
	a?: RearrangeGrowthOptions;
	b?: RearrangeGrowthOptions;
}

/**
 * Positional swap across two panels: column A takes B's slot on B's island and
 * B takes A's slot on A's island. Both islands are fit-checked with the
 * exchanged widths FIRST and the whole swap is refused if either side cannot
 * hold its new column; nothing is mutated on refusal. Both columns are removed
 * before either is re-added, so neither island is ever transiently over-full.
 */
export const swapApplianceGroupPositionsAcrossPanelsCfn = (kitchen: Kitchen, a: PanelItemGroup, b: PanelItemGroup, options: SwapAcrossPanelsOptions = {}): SwapGroupsAcrossPanelsResult => {
	const panelA = a.refMap.panel as Panel | null;
	const panelB = b.refMap.panel as Panel | null;
	const islandA = a.refMap.island as Island | null;
	const islandB = b.refMap.island as Island | null;
	if (!panelA || !panelB || !islandA || !islandB) {
		throw new Error('swapApplianceGroupPositionsAcrossPanels: both groups must belong to islands.');
	}

	if (panelA.id === panelB.id) {
		panelA.swapApplianceGroupPositions(a.id, b.id);
		return { movedIds: [...a.items.children, ...b.items.children].map((pi) => pi.id), resized: [] };
	}

	const carryA = takeGroupCarryCfn(a);
	const carryB = takeGroupCarryCfn(b);
	const xA = a.center.x;
	const xB = b.center.x;

	// A's island loses A and gains B; B's island loses B and gains A
	const growthA = resolveGrowthCfn(islandA, predictPanelGrowthCfn(islandA, panelA, hypotheticalGroupWidthsCfn(panelA, [a.id], [carryB.width])), options.a);
	const growthB = resolveGrowthCfn(islandB, predictPanelGrowthCfn(islandB, panelB, hypotheticalGroupWidthsCfn(panelB, [b.id], [carryA.width])), options.b);

	removeGroupCfn(kitchen, a, carryA);
	removeGroupCfn(kitchen, b, carryB);
	placeGroupCarryCfn(kitchen, panelB, carryA, xB);
	placeGroupCarryCfn(kitchen, panelA, carryB, xA);
	panelA.resetFineTune();
	panelB.resetFineTune();

	const resized: Array<IslandResize> = [];
	const toA = cleanIslandCfn(islandA, { autoResize: growthA !== null, growOnly: true });
	if (toA !== null && growthA) resized.push({ islandId: islandA.id, fromWidthIn: growthA.fromWidthIn, toWidthIn: toA });
	const toB = cleanIslandCfn(islandB, { autoResize: growthB !== null, growOnly: true });
	if (toB !== null && growthB) resized.push({ islandId: islandB.id, fromWidthIn: growthB.fromWidthIn, toWidthIn: toB });

	return { movedIds: [...carryA.movedIds, ...carryB.movedIds], resized };
};
